import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateHreflangTags } from '../../utils/hreflang';

const faqItems = [
  {
    question: "Do you sell organs?",
    answer: "No. We do not engage in organ trade or any form of organ commercialization. We provide coordination and information support only. All transplant procedures are conducted by licensed hospitals under local transplant laws."
  },
  {
    question: "Do you make medical decisions for patients?",
    answer: "No. We do not make medical decisions. All clinical decisions, including eligibility, treatment plans, and surgical procedures, are made by licensed physicians at the treating hospital. Our role is limited to coordination and information support."
  },
  {
    question: "Are you a hospital or medical provider?",
    answer: "No. We are not a hospital or medical provider. We do not perform surgeries, provide medical treatment, or offer medical diagnosis. We coordinate with licensed hospitals and provide information support to patients."
  },
  {
    question: "How do you ensure legal compliance?",
    answer: "We operate in accordance with applicable international and local laws in all countries where patients receive medical treatment. We only coordinate with hospitals that operate under recognized regulatory oversight and comply with local transplant regulations."
  },
  {
    question: "What are your fees?",
    answer: "All costs are clearly disclosed before engagement. Our coordination fees are separated from hospital and medical costs. There are no hidden fees or performance-based medical guarantees. We provide full cost transparency."
  },
  {
    question: "How is patient data protected?",
    answer: "All patient information is treated as strictly confidential and handled securely. Personal medical information is shared only with relevant medical institutions when authorized by the patient. We follow reasonable data protection practices."
  },
  {
    question: "Should I consult my own doctor?",
    answer: "Yes, absolutely. We strongly encourage all patients to seek independent medical advice, review hospital credentials, understand risks and benefits, and make informed decisions voluntarily. Always consult a licensed physician for medical advice."
  },
  {
    question: "Do you guarantee transplant outcomes?",
    answer: "No. We do not guarantee transplant outcomes, specific timelines, or medical results. Kidney transplantation involves inherent medical risks, and outcomes depend on individual patient conditions and medical factors determined by treating physicians."
  }
];

export default function EthicsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Ethical & Legal Framework | International Kidney Transplant Support';

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('description', 'Our international kidney transplant support services operate under strict ethical standards, legal compliance, and patient transparency principles.');
    updateMetaTag('keywords', 'kidney transplant ethics, legal compliance kidney transplant, ethical kidney transplant abroad, organ transplant legal framework, patient data protection, transplant coordination ethics');
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    updateLinkTag('canonical', 'https://kidneypatientsupport.com/ethics');

    // Hreflang tags
    updateHreflangTags({
      enPath: '/ethics',
      jaPath: '/ethics' // Japanese ethics page exists
    });

    updateMetaTag('og:url', 'https://kidneypatientsupport.com/ethics', true);
    updateMetaTag('og:title', 'Ethical & Legal Framework | International Kidney Transplant Support', true);
    updateMetaTag('og:description', 'Our international kidney transplant support services operate under strict ethical standards, legal compliance, and patient transparency principles.', true);
    updateMetaTag('og:type', 'website', true);

    updateMetaTag('twitter:url', 'https://kidneypatientsupport.com/ethics');
    updateMetaTag('twitter:title', 'Ethical & Legal Framework | International Kidney Transplant Support');
    updateMetaTag('twitter:description', 'Our international kidney transplant support services operate under strict ethical standards, legal compliance, and patient transparency principles.');
    updateMetaTag('twitter:card', 'summary_large_image');

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kidneypatientsupport.com/" },
        { "@type": "ListItem", "position": 2, "name": "Ethics & Legal Framework", "item": "https://kidneypatientsupport.com/ethics/" }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Ethics & Legal Framework",
      "description": "Our international kidney transplant support services operate under strict ethical standards, legal compliance, and patient transparency principles.",
      "url": "https://kidneypatientsupport.com/ethics/",
      "inLanguage": "en",
      "isPartOf": { "@type": "WebSite", "@id": "https://kidneypatientsupport.com/#website" },
      "publisher": { "@id": "https://kidneypatientsupport.com/#organization" }
    });
    document.head.appendChild(webPageScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    });
    document.head.appendChild(faqScript);

    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (breadcrumbScript.parentNode) document.head.removeChild(breadcrumbScript);
      if (webPageScript.parentNode) document.head.removeChild(webPageScript);
      if (faqScript.parentNode) document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png" alt="Kidney Patient Support" className="h-12 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Home</Link>
            <Link to="/complete-guide-kidney-transplant-abroad" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Guide</Link>
            <Link to="/countries" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Countries</Link>
            <Link to="/process" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Process</Link>
            <Link to="/pricing" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Pricing</Link>
            <Link to="/ethics" className="text-sm font-medium text-teal-600 transition-colors whitespace-nowrap">Ethics</Link>
            <Link to="/company" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Company</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">Contact</Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <i className="ri-close-line text-2xl"></i> : <i className="ri-menu-line text-2xl"></i>}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-[55] overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Home</Link>
              <Link to="/countries" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Countries</Link>
              <Link to="/process" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Process</Link>
              <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Pricing</Link>
              <Link to="/ethics" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Ethics</Link>
              <Link to="/company" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Company</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/22c6172e02d562d6e564dfd24e31c2e8.jpg"
            alt="Ethics and legal compliance framework for international kidney transplant coordination"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center py-28 w-full">
          <div className="inline-block bg-teal-600/20 backdrop-blur-sm border border-teal-400/30 text-white px-6 py-2 rounded-full mb-6">
            <span className="text-sm font-semibold tracking-wide">LEGAL &amp; ETHICAL FRAMEWORK</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Ethics &amp; Legal Framework
          </h1>
          <p className="text-xl text-white/95 leading-relaxed max-w-3xl mx-auto mb-4">
            <strong>We are committed to operating within the highest ethical and legal standards in providing international kidney transplant coordination and information support services.</strong>
          </p>
          <p className="text-lg text-white/85 leading-relaxed max-w-3xl mx-auto">
            Our role is limited to patient support, coordination assistance, and information facilitation. We do not provide medical treatment, perform surgeries, or make medical decisions.
          </p>
        </div>
      </section>

      {/* Legal Compliance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              1. Legal <span className="text-teal-600">Compliance</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              We operate in accordance with applicable international and local laws in all countries where patients receive medical treatment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-circle-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">We Do Not Engage in Organ Trade</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    We do not buy, sell, or facilitate the commercialization of human organs in any form.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-cross-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">No Illegal Donor Arrangements</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    We do not broker illegal donor arrangements or participate in any activity that violates transplant regulations.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-hospital-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Licensed Hospitals Only</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    All medical procedures are conducted exclusively by licensed hospitals and qualified transplant surgeons.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8 border border-teal-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-heart-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Patient Responsibility</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Patients are responsible for consulting directly with licensed medical professionals before making any treatment decisions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scope of Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              2. Scope of Our <span className="text-teal-600">Services</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What We Do */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="w-14 h-14 bg-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-checkbox-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Services Include</h3>
              <ul className="space-y-4">
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Providing general information about transplant options</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Coordinating communication with hospitals (when requested)</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Assisting with logistical preparation</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Offering cost transparency guidance</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-checkbox-circle-fill text-teal-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Supporting document preparation</span>
                </li>
              </ul>
            </div>

            {/* What We Don't Do */}
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-close-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">We Do NOT</h3>
              <ul className="space-y-4">
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-circle-fill text-red-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Provide medical diagnosis</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-circle-fill text-red-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Provide medical prescriptions</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-circle-fill text-red-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Guarantee transplant outcomes</span>
                </li>
                <li className="flex items-start text-gray-700 text-base">
                  <i className="ri-close-circle-fill text-red-600 mt-1 mr-3 flex-shrink-0 text-lg"></i>
                  <span>Make medical recommendations</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 rounded-2xl p-6">
            <p className="text-base text-gray-800 leading-relaxed font-semibold">
              <i className="ri-information-line text-blue-600 mr-2"></i>
              All medical decisions remain solely between the patient and their treating physician.
            </p>
          </div>
        </div>
      </section>

      {/* Donor and Ethical Standards Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              3. Donor and <span className="text-teal-600">Ethical Standards</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              We strictly respect international transplant ethics principles, including:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-3xl p-8 border border-green-200">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-hand-heart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Voluntary Donor Participation</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                All donor participation must be completely voluntary, with full informed consent and without coercion.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-3xl p-8 border border-green-200">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-scales-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compliance with Local Transplant Laws</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                All programs operate under the legal frameworks and transplant regulations of their respective countries.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-3xl p-8 border border-green-200">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-user-smile-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Respect for Human Dignity</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                The dignity and rights of both donors and recipients are paramount in every program we coordinate.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-3xl p-8 border border-green-200">
              <div className="w-14 h-14 bg-green-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-close-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No Commercialization of Organs</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                We do not support or facilitate any form of organ commercialization or financial exploitation.
              </p>
            </div>
          </div>

          <div className="mt-8 bg-green-50 border-l-4 border-green-600 rounded-2xl p-6">
            <p className="text-base text-gray-800 leading-relaxed font-semibold">
              <i className="ri-shield-check-line text-green-600 mr-2"></i>
              We only coordinate with hospitals that operate under recognized regulatory oversight.
            </p>
          </div>
        </div>
      </section>

      {/* Transparency & Financial Clarity Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              4. Transparency &amp; <span className="text-teal-600">Financial Clarity</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              We believe patients deserve full transparency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Clear Cost Disclosure</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Estimated costs are clearly disclosed before any engagement or commitment.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Defined Service Scope</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Service scope is clearly defined before engagement, with no ambiguity about what is included.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">No Hidden Fees</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    All costs are disclosed upfront. There are no hidden charges or surprise fees.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-cross-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">No Performance Guarantees</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    We do not offer performance-based medical guarantees or promise specific outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Patient Privacy & Data Protection Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              5. Patient Privacy &amp; <span className="text-teal-600">Data Protection</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              We treat all patient information as strictly confidential.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border border-purple-200">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Secure Handling</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Personal medical information is handled securely with appropriate safeguards.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border border-purple-200">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Authorized Sharing Only</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                Information is shared only with relevant medical institutions when authorized by the patient.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border border-purple-200">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-file-shield-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Data Protection Practices</h3>
              <p className="text-gray-700 text-base leading-relaxed">
                We follow reasonable data protection practices to safeguard patient information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Independent Medical Decision-Making Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              6. Independent Medical <span className="text-teal-600">Decision-Making</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              Patients are strongly encouraged to:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-stethoscope-line text-orange-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Seek Independent Medical Advice</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Consult with your own licensed physician or nephrologist before making any treatment decisions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-hospital-line text-orange-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Review Hospital Credentials</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Verify the credentials, accreditation, and reputation of any hospital or medical facility.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-orange-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Understand Risks and Benefits</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Fully understand the medical risks, benefits, and alternatives before proceeding.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-heart-line text-orange-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Make Informed Decisions Voluntarily</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    All decisions should be made voluntarily, without pressure, and with full understanding.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-600 rounded-2xl p-6">
            <p className="text-base text-gray-800 leading-relaxed font-semibold">
              <i className="ri-alert-line text-orange-600 mr-2"></i>
              We do not influence medical judgment. All clinical decisions remain between you and your treating physician.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              7. <span className="text-teal-600">Disclaimer</span>
            </h2>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-3xl p-10">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Informational Content Only</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    This website provides general informational content only. It does not constitute medical advice, legal advice, or treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-stethoscope-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Not Medical Advice</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    Information provided should not be considered as medical advice, diagnosis, or treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-hospital-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Consult Licensed Physicians</h3>
                  <p className="text-gray-700 text-base leading-relaxed">
                    For medical advice, always consult a licensed physician or qualified healthcare professional.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked <span className="text-teal-600">Questions</span>
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto">
              Clear answers to common questions about our ethical standards and legal compliance.
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className={`bg-white rounded-2xl border transition-all ${openFaq === index ? 'border-teal-300 shadow-md' : 'border-gray-200'}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{item.question}</h3>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-xl transition-transform`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-700 text-base leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Questions About Our Ethics or Legal Compliance?
          </h2>
          <p className="text-xl text-white/95 mb-10 leading-relaxed max-w-3xl mx-auto">
            Transparency is not just a policy — it is our foundation. If you have questions about our ethical standards, legal compliance, or the scope of our services, we welcome your inquiry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-white text-teal-600 px-10 py-5 rounded-full text-lg font-bold hover:bg-gray-50 transition-colors space-x-3 shadow-xl whitespace-nowrap cursor-pointer"
            >
              <span>Contact Us</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/privacy"
              className="inline-flex items-center justify-center bg-white/15 backdrop-blur-sm border border-white/30 text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-white/25 transition-colors space-x-3 whitespace-nowrap cursor-pointer"
            >
              <span>Privacy Policy</span>
              <i className="ri-shield-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <h3 className="text-4xl font-bold mb-6">Japan Ambis International</h3>
              <p className="text-gray-400 text-base leading-relaxed mb-6 max-w-md">
                Dedicated to improving the lives of kidney patients through comprehensive support coordination, education, and connecting patients with trusted international medical programs.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-500">QUICK LINKS</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/countries" className="text-gray-400 hover:text-white transition-colors">Countries</Link></li>
                <li><Link to="/process" className="text-gray-400 hover:text-white transition-colors">Process</Link></li>
                <li><Link to="/ethics" className="text-gray-400 hover:text-white transition-colors">Ethics</Link></li>
                <li><Link to="/company" className="text-gray-400 hover:text-white transition-colors">Company</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-500">PROGRAMS</h4>
              <ul className="space-y-3">
                <li><Link to="/india" className="text-gray-400 hover:text-white transition-colors">India</Link></li>
                <li><Link to="/cambodia" className="text-gray-400 hover:text-white transition-colors">Cambodia</Link></li>
                <li><Link to="/pakistan" className="text-gray-400 hover:text-white transition-colors">Pakistan</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              &copy; 2025 Japan Ambis International. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</Link>
              <Link to="/ethics" className="text-gray-500 hover:text-white text-sm transition-colors">Ethics</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white text-sm transition-colors">Web Design</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
